import { useEffect, useState } from "react";
import { Plus, Trash2, Save, Star, StarOff, Pencil, X } from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  createContact,
  deleteContact,
  listContacts,
  updateContact,
  type EmergencyContact,
  type EmergencyContactCreate,
} from "@/api/emergencyContacts";
import { useToast } from "@/hooks/useToast";

interface Props {
  employeeId: string;
  /** Legacy prop kept for call-site compatibility — no longer read. */
  initial?: unknown[] | null;
  canEdit: boolean;
}

type FormState = {
  name: string;
  relationship: string;
  phone: string;
  phone_secondary: string;
  email: string;
  address: string;
  notes: string;
  is_primary: boolean;
};

const EMPTY_FORM: FormState = {
  name: "",
  relationship: "",
  phone: "",
  phone_secondary: "",
  email: "",
  address: "",
  notes: "",
  is_primary: false,
};

function contactToForm(c: EmergencyContact): FormState {
  return {
    name: c.name,
    relationship: c.relationship ?? "",
    phone: c.phone,
    phone_secondary: c.phone_secondary ?? "",
    email: c.email ?? "",
    address: c.address ?? "",
    notes: c.notes ?? "",
    is_primary: c.is_primary,
  };
}

function formToPayload(f: FormState): EmergencyContactCreate {
  return {
    name: f.name.trim(),
    relationship: f.relationship.trim() || null,
    phone: f.phone.trim(),
    phone_secondary: f.phone_secondary.trim() || null,
    email: f.email.trim() || null,
    address: f.address.trim() || null,
    notes: f.notes.trim() || null,
    is_primary: f.is_primary,
  };
}

export function EmergencyContactsEditor({ employeeId, canEdit }: Props) {
  const { addToast } = useToast();
  const [contacts, setContacts] = useState<EmergencyContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const rows = await listContacts(employeeId);
      setContacts(rows);
    } catch {
      addToast("error", "Failed to load emergency contacts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const startAdd = () => {
    setEditingId(null);
    setAdding(true);
    setForm({ ...EMPTY_FORM, is_primary: contacts.length === 0 });
  };

  const startEdit = (c: EmergencyContact) => {
    setAdding(false);
    setEditingId(c.id);
    setForm(contactToForm(c));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setAdding(false);
    setForm(EMPTY_FORM);
  };

  const save = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      addToast("error", "Name and phone are required");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateContact(employeeId, editingId, formToPayload(form));
        addToast("success", "Contact updated");
      } else {
        await createContact(employeeId, formToPayload(form));
        addToast("success", "Contact added");
      }
      cancelEdit();
      await load();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: unknown } } };
      const detail = ax.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail) && detail[0]?.msg
            ? String(detail[0].msg)
            : "Failed to save contact";
      addToast("error", msg);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c: EmergencyContact) => {
    if (!window.confirm(`Remove emergency contact "${c.name}"?`)) return;
    try {
      await deleteContact(employeeId, c.id);
      addToast("success", "Contact removed");
      await load();
    } catch {
      addToast("error", "Failed to remove contact");
    }
  };

  const promote = async (c: EmergencyContact) => {
    try {
      await updateContact(employeeId, c.id, { is_primary: true });
      await load();
    } catch {
      addToast("error", "Failed to set primary");
    }
  };

  const renderForm = () => (
    <div className="rounded-lg border border-border/60 bg-surface-elevated/40 p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input
          label="Name *"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <Input
          label="Relationship"
          value={form.relationship}
          onChange={(e) =>
            setForm({ ...form, relationship: e.target.value })
          }
          placeholder="Spouse, Parent, ..."
        />
        <Input
          label="Phone *"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
        <Input
          label="Phone (secondary)"
          value={form.phone_secondary}
          onChange={(e) =>
            setForm({ ...form, phone_secondary: e.target.value })
          }
        />
        <Input
          label="Email"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <Input
          label="Address"
          value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
        />
      </div>
      <Input
        label="Notes"
        value={form.notes}
        onChange={(e) => setForm({ ...form, notes: e.target.value })}
      />
      <label className="flex items-center gap-2 text-sm text-text-primary">
        <input
          type="checkbox"
          checked={form.is_primary}
          onChange={(e) =>
            setForm({ ...form, is_primary: e.target.checked })
          }
          className="rounded border-border text-accent"
        />
        Primary contact
      </label>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="ghost" onClick={cancelEdit} disabled={saving}>
          <X className="h-4 w-4" />
          Cancel
        </Button>
        <Button onClick={save} isLoading={saving}>
          <Save className="h-4 w-4" />
          Save
        </Button>
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Emergency contacts</CardTitle>
        {canEdit && !adding && !editingId && (
          <Button size="sm" variant="ghost" onClick={startAdd}>
            <Plus className="h-4 w-4" />
            Add
          </Button>
        )}
      </CardHeader>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : (
        <div className="space-y-4">
          {contacts.length === 0 && !adding && (
            <p className="text-sm text-text-muted text-center py-6">
              No emergency contacts on file
            </p>
          )}

          {contacts.map((c) =>
            editingId === c.id ? (
              <div key={c.id}>{renderForm()}</div>
            ) : (
              <div
                key={c.id}
                className="rounded-lg border border-border/50 p-4 flex items-start justify-between gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-text-primary truncate">
                      {c.name}
                    </p>
                    {c.is_primary && (
                      <Badge variant="success">Primary</Badge>
                    )}
                    {c.relationship && (
                      <span className="text-xs text-text-muted">
                        {c.relationship}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-sm text-text-secondary space-y-0.5">
                    <p>{c.phone}</p>
                    {c.phone_secondary && (
                      <p className="text-xs text-text-muted">
                        Alt: {c.phone_secondary}
                      </p>
                    )}
                    {c.email && (
                      <p className="text-xs text-text-muted truncate">
                        {c.email}
                      </p>
                    )}
                    {c.address && (
                      <p className="text-xs text-text-muted truncate">
                        {c.address}
                      </p>
                    )}
                    {c.notes && (
                      <p className="text-xs text-text-muted italic">
                        {c.notes}
                      </p>
                    )}
                  </div>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1 shrink-0">
                    {!c.is_primary && (
                      <button
                        title="Make primary"
                        onClick={() => promote(c)}
                        className="p-1.5 rounded hover:bg-surface-elevated text-text-muted hover:text-accent"
                      >
                        <StarOff className="h-4 w-4" />
                      </button>
                    )}
                    {c.is_primary && (
                      <span
                        title="Primary contact"
                        className="p-1.5 text-accent"
                      >
                        <Star className="h-4 w-4 fill-current" />
                      </span>
                    )}
                    <button
                      title="Edit"
                      onClick={() => startEdit(c)}
                      className="p-1.5 rounded hover:bg-surface-elevated text-text-muted hover:text-text-primary"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      title="Remove"
                      onClick={() => remove(c)}
                      className="p-1.5 rounded hover:bg-danger-50 text-text-muted hover:text-danger"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            )
          )}

          {adding && renderForm()}
        </div>
      )}
    </Card>
  );
}
